import React, { useMemo } from 'react';
import ReadOnlyField from 'v3/Components/Form/ReadOnlyField';
import { useIdParam, useLineOfBusinessParam } from 'Utilities';
import { parseRawDateString, parseUtcToLocal } from 'Utilities/DateTimeUtc';
import { TitleActive } from 'v3/Components/Form/TitleActive';
import { TitleReadOnly } from 'v3/Components/Form/TitleReadOnly';
import { InputActive } from 'v3/Components/Form/InputActive';
import './AddEditReferralsRequest.AdditionalInformation.scss';
import { FieldError } from 'v3/Components/Form/FormSection';
import {
  createReferralRequestSchema,
  useReferralRequestForm,
} from './useReferralRequestForm';
import './AddEditReferralsRequest.ClaimDetails.scss';
import Dropdown from 'v3/Components/Form/Dropdown';
import { useEnums } from 'Context';
import { formatDate } from '@telerik/kendo-intl';
import { DateTimePickerInput } from 'v3/Components/Form/DateTimePickers/DateTimePicker';
import { LineOfBusinessEnum } from 'v3/Models/Enums';
import { Render } from 'Components/Display';
import { ReplicableComponent } from '../../Form/ReplicableComponent';

type ClaimDetailsProps = {
  form: ReturnType<typeof useReferralRequestForm>;
  fieldErrors?: FieldError[];
};

export const ClaimDetailsActive: React.FC<ClaimDetailsProps> = ({
  form,
  fieldErrors,
}) => {
  const { state } = useEnums();
  const { lob } = useLineOfBusinessParam();
  const isWorkersComp = lob === LineOfBusinessEnum.WorkersComp.toString();
  const isIME = lob === LineOfBusinessEnum.IME.toString();
  const isDisability = lob === LineOfBusinessEnum.Disability.toString();
  const isHealthReview = lob === LineOfBusinessEnum.HealthReview.toString();
  const isPharmacy = lob === LineOfBusinessEnum.Pharmacy.toString();
  const isImpairmentRatingReview =
    lob === LineOfBusinessEnum.ImpairmentRatingReview.toString();

  const ReferralRequestSchema = useMemo(
    () => createReferralRequestSchema(Number(lob) as LineOfBusinessEnum),
    [lob]
  );

  return (
    <div className="">
      <TitleActive>Claim Details</TitleActive>
      <div className="active-row">
        <div className="active-fields">
          <Render
            condition={
              isWorkersComp ||
              isIME ||
              isDisability ||
              isHealthReview ||
              isPharmacy ||
              isImpairmentRatingReview
            }
          >
            <InputActive
              id="claimNumber"
              name="claimNumber"
              label="Claim/Client Number"
              value={form.getFieldProps('claimNumber').value ?? ''}
              onChange={(e: any) => {
                form.setFieldValue('claimNumber', e.target.value);
              }}
              required
              error={
                fieldErrors?.find((f) => f.fieldId === 'claimNumber')?.error
              }
            />
          </Render>
          <Render
            condition={
              isWorkersComp ||
              isIME ||
              isDisability ||
              isHealthReview ||
              isPharmacy ||
              isImpairmentRatingReview
            }
          >
            <InputActive
              id="referralNumber"
              name="referralNumber"
              label="Referral Number"
              value={form.getFieldProps('referralNumber').value ?? ''}
              onChange={(e: any) => {
                form.setFieldValue('referralNumber', e.target.value);
              }}
              error={
                fieldErrors?.find((f) => f.fieldId === 'referralNumber')?.error
              }
            />
          </Render>
          <Render condition={isWorkersComp || isImpairmentRatingReview}>
            <DateTimePickerInput
              label={'Date of Injury'}
              value={form.getFieldProps('dateOfInjury').value}
              required
              onChange={(value) => form.setFieldValue('dateOfInjury', value)}
              error={
                fieldErrors?.find((f) => f.fieldId === 'dateOfInjury')?.error
              }
              dateOnly
            />
          </Render>
          <Render condition={isHealthReview || isPharmacy}>
            <DateTimePickerInput
              label={'Date and Time Request Received'}
              hint={
                'This is the date that the request was received and TAT clock begins'
              }
              value={form.getFieldProps('dateOfRequest').value}
              onChange={(value) => {
                form.setFieldValue('dateOfRequest', value);
              }}
              error={
                fieldErrors?.find((f) => f.fieldId === 'dateOfRequest')?.error
              }
            />
          </Render>

          <Render condition={isIME || isDisability}>
            <DateTimePickerInput
              label={isDisability ? 'Date of Disability' : 'Date of Injury'}
              value={form.getFieldProps('dateOfInjury').value}
              onChange={(value) => form.setFieldValue('dateOfInjury', value)}
              error={
                fieldErrors?.find((f) => f.fieldId === 'dateOfInjury')?.error
              }
              required={isDisability || isWorkersComp}
              dateOnly
            />
          </Render>
        </div>
      </div>
      <Render condition={isImpairmentRatingReview}>
        <div className="active-row">
          <div className="active-fields">
            <Render condition={isImpairmentRatingReview}>
              <DateTimePickerInput
                label={'Date of Exam'}
                value={form.getFieldProps('dateOfExam').value}
                onChange={(value) => form.setFieldValue('dateOfExam', value)}
                error={
                  fieldErrors?.find((f) => f.fieldId === 'dateOfExam')?.error
                }
                dateOnly
              />
            </Render>
          </div>
        </div>
      </Render>
      <Render
        condition={
          isWorkersComp ||
          isIME ||
          isDisability ||
          isHealthReview ||
          isPharmacy ||
          isImpairmentRatingReview
        }
      >
        <TitleActive>Employer Information</TitleActive>
        <div className="active-row">
          <div className="active-fields">
            <InputActive
              id="employerName"
              name="employerName"
              label="Employer Name"
              value={form.getFieldProps('employerName').value ?? ''}
              onChange={(e: any) => {
                form.setFieldValue('employerName', e.target.value);
              }}
              error={
                fieldErrors?.find((f) => f.fieldId === 'employerName')?.error
              }
              className="input-container street-address"
            />
            <Dropdown
              label="Employer Location"
              value={form.getFieldProps('employerLocation').value}
              onChange={(value) =>
                form.setFieldValue('employerLocation', value)
              }
              options={[]}
              enums={state}
              valueAsDisplayName
              error={
                fieldErrors?.find((f) => f.fieldId === 'employerLocation')
                  ?.error
              }
            />
            <InputActive
              id="employerZipCode"
              name="employerZipCode"
              label="Employer Zip Code"
              value={form.getFieldProps('employerZipCode').value ?? ''}
              onChange={(e: any) => {
                form.setFieldValue('employerZipCode', e.target.value);
              }}
              error={fieldErrors?.find((f) => f.fieldId === 'zipCode')?.error}
            />
          </div>
        </div>
      </Render>

      <Render condition={isPharmacy}>
        <TitleActive>Medication Details</TitleActive>
        <div className="active-row">
          <ReplicableComponent
            form={form}
            label={'Medication'}
            formPropsToClearOnRemoval={['medications']}
            initialDataArray={form.getFieldProps('medications').value ?? []}
            initialData={{
              medicationName: '',
              medicationDetails: '',
            }}
          >
            {({ index, data, onChange, onDuplicate }) => (
              <div
                className="active-fields"
                style={{ justifyContent: 'space-between', width: '100%' }}
              >
                <div style={{ width: '33%' }}>
                  <InputActive
                    id="medicationName"
                    name="medicationName"
                    label="Medication Name"
                    value={
                      form.getFieldProps('medications').value?.[index]
                        ?.medicationName ?? ''
                    }
                    onChange={(e: any) => {
                      onChange(
                        { medicationName: e.target.value },
                        'medications'
                      );
                    }}
                    required
                    error={
                      fieldErrors?.find(
                        (f) => f.fieldId === 'medications[0].MedicationName'
                      )?.error
                    }
                  />
                </div>
                <div style={{ width: '66%' }}>
                  <InputActive
                    id="medicationDetails"
                    name="medicationDetails"
                    label="Medication Details"
                    hint={
                      'Please provide the dosage, form, frequency and any other details to specify the medication'
                    }
                    value={
                      form.getFieldProps('medications').value?.[index]
                        ?.medicationDetails ?? ''
                    }
                    onChange={(e: any) => {
                      onChange(
                        { medicationDetails: e.target.value },
                        'medications'
                      );
                    }}
                    required
                    error={
                      fieldErrors?.find(
                        (f) => f.fieldId === 'medications[0].MedicationDetails'
                      )?.error
                    }
                  />
                </div>
              </div>
            )}
          </ReplicableComponent>
        </div>
      </Render>
    </div>
  );
};

export const ClaimDetailsReadOnly: React.FC<ClaimDetailsProps> = ({ form }) => {
  const { lob } = useLineOfBusinessParam();
  const ReferralRequestSchema = useMemo(
    () => createReferralRequestSchema(Number(lob) as LineOfBusinessEnum),
    [lob]
  );
  const isWorkersComp = lob === LineOfBusinessEnum.WorkersComp.toString();
  const isIME = lob === LineOfBusinessEnum.IME.toString();
  const isDisability = lob === LineOfBusinessEnum.Disability.toString();
  const isHealthReview = lob === LineOfBusinessEnum.HealthReview.toString();
  const isPharmacy = lob === LineOfBusinessEnum.Pharmacy.toString();
  const isImpairmentRatingReview =
    lob === LineOfBusinessEnum.ImpairmentRatingReview.toString();
  return (
    <div className="read-only-content">
      <TitleReadOnly>Claimant Identity</TitleReadOnly>
      <Render
        condition={
          isWorkersComp ||
          isIME ||
          isDisability ||
          isHealthReview ||
          isPharmacy ||
          isImpairmentRatingReview
        }
      >
        <ReadOnlyField
          id="claimNumber"
          label="Claim/Client Number"
          value={form.getFieldProps('claimNumber').value ?? ''}
          schema={ReferralRequestSchema}
        />
      </Render>
      <Render
        condition={
          isWorkersComp ||
          isIME ||
          isDisability ||
          isHealthReview ||
          isPharmacy ||
          isImpairmentRatingReview
        }
      >
        <ReadOnlyField
          id="referralNumber"
          label="Referral Number"
          value={form.getFieldProps('referralNumber').value ?? ''}
          schema={ReferralRequestSchema}
        />
      </Render>
      <Render
        condition={isWorkersComp || isImpairmentRatingReview || isDisability}
      >
        <ReadOnlyField
          id="dateOfInjury"
          forceLabel={isDisability ? 'Date of Disability' : 'Date of Injury'}
          value={
            form.getFieldProps('dateOfInjury').value
              ? formatDate(
                  parseRawDateString(form.getFieldProps('dateOfInjury').value) || new Date(),
                  'd'
                )
              : ''
          }
          schema={ReferralRequestSchema}
        />
      </Render>
      <Render condition={isHealthReview || isPharmacy}>
        <ReadOnlyField
          id="dateOfRequest"
          label="Date and Time Request Received"
          value={
            form.getFieldProps('dateOfRequest').value
              ? formatDate(
                  parseUtcToLocal(form.getFieldProps('dateOfRequest').value) || new Date(),
                  'g'
                )
              : ''
          }
          schema={ReferralRequestSchema}
        />
      </Render>
      <Render condition={isWorkersComp || isImpairmentRatingReview}>
        <ReadOnlyField
          id="dateOfExam"
          label="Date of Exam"
          value={
            form.getFieldProps('dateOfExam').value
              ? formatDate(
                  parseRawDateString(form.getFieldProps('dateOfExam').value) || new Date(),
                  'd'
                )
              : ''
          }
          schema={ReferralRequestSchema}
        />
      </Render>
      <Render condition={isIME}>
        <ReadOnlyField
          id="dateOfInjury"
          label="Date of Disability"
          value={
            form.getFieldProps('dateOfInjury').value
              ? formatDate(
                  parseRawDateString(form.getFieldProps('dateOfInjury').value) || new Date(),
                  'd'
                )
              : ''
          }
          schema={ReferralRequestSchema}
        />
      </Render>
      <Render
        condition={
          isWorkersComp ||
          isIME ||
          isDisability ||
          isHealthReview ||
          isPharmacy ||
          isImpairmentRatingReview
        }
      >
        <TitleReadOnly>Employer Information</TitleReadOnly>
        <ReadOnlyField
          id="employerName"
          label="Employer Name"
          value={form.getFieldProps('employerName').value ?? ''}
          schema={ReferralRequestSchema}
        />
        <ReadOnlyField
          id="employerLocation"
          label="Employer Location"
          value={form.getFieldProps('employerLocation.label').value ?? ''}
          schema={ReferralRequestSchema}
        />
        <ReadOnlyField
          id="employerZipCode"
          label="Employer Zip Code"
          value={form.getFieldProps('employerZipCode').value ?? ''}
          schema={ReferralRequestSchema}
        />
      </Render>
      <Render condition={isPharmacy}>
        {form
          .getFieldProps('medications')
          .value?.map((medication: any, index: number) => (
            <div key={index} style={{ marginTop: 38 }}>
              <TitleReadOnly>
                Medication{' '}
                {form.getFieldProps('medications').value?.length > 1
                  ? `${index + 1}`
                  : ''}{' '}
                Details
              </TitleReadOnly>
              <ReadOnlyField
                id="medications"
                label="Medication Name"
                value={medication?.medicationName ?? ''}
                forceLabel="Medication Name"
                schema={ReferralRequestSchema}
              />
              <ReadOnlyField
                id="medications"
                label="Medication Details"
                value={medication?.medicationDetails ?? ''}
                forceLabel="Medication Details"
                schema={ReferralRequestSchema}
              />
            </div>
          ))}
      </Render>
    </div>
  );
};
